import React from 'react';
import { createRoot } from 'react-dom/client';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { defineCustomElements as defineJeepSqlite } from 'jeep-sqlite/loader';
import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import { installPwaVersionSkewRecovery } from './pwaVersionSkewRecovery';

// Reuse the mobile React app — the web entrypoint just mounts it and wires
// up PWA bits (manifest + service worker via vite-plugin-pwa).
import { App } from '@trackeat/mobile/App';
import '@trackeat/mobile/lib/i18n/index';

import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

import '@trackeat/mobile/theme/variables.css';
import '@trackeat/mobile/theme/unified-ui.css';

// Espone la build effettivamente avviata senza leggere storage o stato utente.
// È anche un segnale diagnostico per distinguere un bundle JS eseguito da un
// index.html rimasto temporaneamente senza il relativo asset durante un deploy.
document.documentElement.dataset.appBuild =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0';
document.documentElement.dataset.uiRelease = 't500-beta';

const isNativePlatform = Capacitor.isNativePlatform();
// Esposto prima del render per consentire ai modali di adattarsi al browser
// desktop senza cambiare il comportamento della WebView nativa.
document.documentElement.dataset.teRuntime = isNativePlatform ? 'native' : 'browser';
// Il recupero e' soltanto per il browser: intercetta l'eccezione Vite quando
// una vecchia shell prova un chunk che non esiste piu e ricarica una sola volta.
// Non pulisce SW, cache, SQLite o outbox, quindi non puo perdere dati offline.
if (!isNativePlatform) installPwaVersionSkewRecovery();

async function bootstrapWebSqlite(): Promise<void> {
  if (Capacitor.getPlatform() !== 'web') return;
  await defineJeepSqlite(window);
  await customElements.whenDefined('jeep-sqlite');
  let el = document.querySelector('jeep-sqlite') as HTMLElement | null;
  if (!el) {
    el = document.createElement('jeep-sqlite');
    document.body.appendChild(el);
  }
  (el as unknown as { wasmPath: string }).wasmPath = '/assets';
  await new Promise<void>((resolve) => {
    const check = () => {
      if ((el as unknown as { componentOnReady?: () => Promise<void> }).componentOnReady) resolve();
      else setTimeout(check, 20);
    };
    check();
  });
  const sqlite = new SQLiteConnection(CapacitorSQLite);
  await sqlite.initWebStore();
}

const container = document.getElementById('root');
if (!container) throw new Error('#root missing');
const root = createRoot(container);

void bootstrapWebSqlite()
  .catch((err) => {
    console.error('[niveo-web] jeep-sqlite bootstrap failed', err);
  })
  .finally(() => {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  });

void defineCustomElements(window).catch(() => { /* optional */ });

// Service Worker: lo registriamo SOLO quando giriamo in browser (PWA).
// Dentro Capacitor (APK Niveo) il SW non deve partire: la WebView ricarica
// la stessa origin Tailscale e un SW registrato lì interferisce con il
// caricamento al secondo avvio (cache offline incompleta → crash).
if (!isNativePlatform) {
  if ('serviceWorker' in navigator) {
    // Aggiornamento controllato della PWA:
    // (1) ricontrolliamo il SW subito, periodicamente e al ritorno in primo piano;
    // (2) quando Workbox segnala una nuova versione, la lasciamo in attesa: la
    // sessione aperta resta coerente e il worker nuovo entra al prossimo riavvio
    // sicuro, senza reclamare una pagina con chunk della release precedente.
    void import('virtual:pwa-register').then(({ registerSW }) => {
      registerSW({
        immediate: true,
        onNeedRefresh() {
          document.documentElement.dataset.pwaUpdate = 'waiting';
        },
        onRegisteredSW(_swUrl, r) {
          if (!r) return;
          const check = (): void => { void r.update().catch(() => { /* offline: riprova dopo */ }); };
          check();
          setInterval(check, 60 * 1000);
          document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
        },
      });
    }).catch(() => { /* PWA non critica */ });
  }
} else {
  // Native: assicurati che nessun SW residuo dalla PWA installata in passato
  // resti attivo dentro la WebView, e svuota la cache HTTP del SW.
  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.getRegistrations().then((rs) => {
      rs.forEach((r) => { void r.unregister(); });
    });
  }
  if (typeof caches !== 'undefined') {
    void caches.keys().then((keys) => {
      keys.forEach((k) => { void caches.delete(k); });
    });
  }
}

// Global crash logger — utile a debug futuro su WebView Capacitor.
window.addEventListener('error', (ev) => {
  console.error('[niveo] window.error', ev.message, ev.error);
});
window.addEventListener('unhandledrejection', (ev) => {
  console.error('[niveo] unhandled rejection', ev.reason);
});
